import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, MapPin, BadgeCheck } from "lucide-react";
import { LeadForm } from "@/components/LeadForm";

const TRUST_POINTS = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: BadgeCheck, label: "Free Inspection Included" },
  { icon: Clock, label: "Same-Week Appointments" },
  { icon: MapPin, label: "Greater PDX Area" },
];

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, index, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: EASE, delay: 0.2 + index * 0.14 }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0.45, 1], [1, 0.3]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative pt-16 overflow-hidden">
      <motion.div className="hero-bg absolute -inset-y-20 inset-x-0" style={{ y: bgY }} />
      <div className="absolute inset-0 bg-black/55" />
      <motion.div
        style={{ opacity: fade }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 space-y-8">
            <motion.span
              data-testid="hero-special-badge"
              className="inline-block bg-primary text-white text-xs uppercase tracking-[0.12em] leading-relaxed font-bold px-4 py-2 rounded-md max-w-xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              Emergency Escrow Service — Callback Within 24 Hours, Service Within 48 — Real Estate Escrow & Home Inspection Punchlist Experts
            </motion.span>
            <h1
              data-testid="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-white font-extrabold"
            >
              <MaskedLine index={0}>$249 Chimney Sweep</MaskedLine>
              <MaskedLine index={1} className="text-3xl sm:text-4xl lg:text-5xl mt-3 font-bold text-white/90">
                Free Inspection Included
              </MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subcopy"
              className="text-base md:text-lg text-white/80 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            >
              Don't let hidden creosote buildup or structural damage threaten your home.
              Our comprehensive sweep removes dangerous fire hazards and blockages, while
              our free, top-to-bottom inspection evaluates your masonry, crowns, and
              flashing to catch minor issues before they become costly repairs.
            </motion.p>
            <motion.div
              className="grid grid-cols-2 gap-4 max-w-md"
              data-testid="hero-trust-points"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
            >
              {TRUST_POINTS.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-white/90">
                  <Icon className="w-5 h-5 text-primary" strokeWidth={2} />
                  <span className="text-sm font-semibold">{label}</span>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.5 }}
          >
            <div
              id="book"
              data-testid="book-now-card"
              className="bg-white rounded-lg p-8 shadow-2xl scroll-mt-24"
            >
              <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">
                  Book Now
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-foreground">
                  Lock In Your $249 Sweep
                </h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Free inspection included with every sweep. We serve Portland,
                  Hillsboro, Clackamas, Beaverton & Tualatin.
                </p>
              </div>
              <LeadForm variant="booking" />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
